import { useMemo, useState, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { publicSupabaseConfig } from "../domain/env";
import type { ParentInvite } from "../domain/parentClaim";
import { useAuth } from "../state/AuthProvider";
import {
  formatBirthdayLabel,
  staff,
} from "../domain/data";
import { answersFromStudent, useForms } from "../state/forms";
import {
  previewStudentImport,
  studentImportTemplateCsv,
  type DuplicateImportRow,
} from "../domain/importStudents";
import { useStudents } from "../state/students";
import { ErrorFlash } from "../components/ErrorFlash";
import { PrototypeBanner, StaffNav } from "../components/ui";

export function StaffUploadStudents() {
  const { students, importStudents } = useStudents();
  const { currentForm, importSubmissions, submissions } = useForms();
  const { ensureStudentAccounts, linkMigratedParents } = useAuth();
  const hosted = publicSupabaseConfig() !== null;
  const [fileName, setFileName] = useState("");
  const [paste, setPaste] = useState("");
  const [preview, setPreview] = useState<ReturnType<
    typeof previewStudentImport
  > | null>(null);
  const [overwrite, setOverwrite] = useState<Record<string, boolean>>({});
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [invites, setInvites] = useState<ParentInvite[]>([]);
  const [inviteSkips, setInviteSkips] = useState<
    { email: string; reason: string }[]
  >([]);

  const selectedDuplicates = useMemo(
    () => preview?.duplicates.filter((row) => overwrite[row.key]) ?? [],
    [overwrite, preview],
  );
  const formAnswerCount =
    (preview?.news.filter((row) => row.parsed.permission).length ?? 0) +
    (preview?.duplicates.filter((row) => row.parsed.permission).length ?? 0);
  const rosterCount = (preview?.news.length ?? 0) + selectedDuplicates.length;

  function downloadTemplate() {
    const blob = new Blob([studentImportTemplateCsv()], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "lift-student-import.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function reviewCsv(text: string, label: string) {
    setError("");
    setResult("");
    const next = previewStudentImport(text, students);
    setFileName(label);
    setPreview(next);
    setOverwrite(
      Object.fromEntries(next.duplicates.map((row) => [row.key, false])),
    );
  }

  function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => reviewCsv(String(reader.result ?? ""), file.name);
    reader.onerror = () => setError("Could not read that file.");
    reader.readAsText(file);
  }

  function onPasteReview() {
    if (!paste.trim()) {
      setError("Paste CSV rows, or choose a file.");
      return;
    }
    reviewCsv(paste, "Pasted CSV");
  }

  function setAllOverwrites(value: boolean) {
    if (!preview) return;
    setOverwrite(
      Object.fromEntries(preview.duplicates.map((row) => [row.key, value])),
    );
  }

  async function importRows() {
    if (!preview) return;
    const rows = [
      ...preview.news.map((row) => ({ input: row.parsed.input })),
      ...selectedDuplicates.map((row) => ({
        input: row.parsed.input,
        existingId: row.existing.id,
      })),
    ];
    const formMatches = preview.duplicates.filter((row) => row.parsed.permission);
    if (rows.length === 0 && formMatches.length === 0) {
      setError("Choose students to add, or check duplicates to overwrite.");
      return;
    }

    const incoming = [...preview.news, ...selectedDuplicates];
    let saved: Awaited<ReturnType<typeof importStudents>> = [];
    try {
      if (rows.length > 0) saved = await importStudents(rows);
      if (currentForm) {
        const filed = [
          ...saved.flatMap((student, index) => {
            const parsed = incoming[index]?.parsed;
            if (!parsed) return [];
            return [{ student, parsed }];
          }),
          ...formMatches
            .filter((row) => !overwrite[row.key])
            .map((row) => ({ student: row.existing, parsed: row.parsed })),
        ];
        if (filed.length > 0) {
          await importSubmissions(
            filed.map(({ student, parsed }) => {
              const prior = submissions.find(
                (item) => item.formId === currentForm.id && item.studentId === student.id,
              );
              return {
                formId: currentForm.id,
                studentId: student.id,
                answers: answersFromStudent(
                  student,
                  parsed.consents,
                  prior?.answers.extraAnswers ?? {},
                  parsed.permission,
                ),
              };
            }),
          );
        }
      }
    } catch {
      setError("Could not save that roster.");
      return;
    }
    ensureStudentAccounts(saved);
    const linked = hosted
      ? { invites: [], skipped: [] }
      : linkMigratedParents(saved);
    setInvites(linked.invites);
    setInviteSkips(linked.skipped);

    const added = preview.news.length;
    const updated = selectedDuplicates.length;
    const skipped = (preview.duplicates.length ?? 0) - updated;
    const formsSaved = currentForm
      ? preview.news.filter((row) => row.parsed.permission).length + formMatches.length
      : 0;
    setResult(
      [
        added ? `${added} student${added === 1 ? "" : "s"} added` : null,
        updated ? `${updated} overwritten` : null,
        skipped && formsSaved === 0
          ? `${skipped} duplicate${skipped === 1 ? "" : "s"} left unchanged`
          : null,
        formsSaved
          ? `Form answers saved for ${formsSaved} student${formsSaved === 1 ? "" : "s"}`
          : null,
        currentForm
          ? `This year's form (${currentForm.year}) is marked complete so families do not register again.`
          : "No open school-year form, so only the roster was updated.",
        hosted && saved.length > 0
          ? "Parent logins are issued with npm run link:parents. Each guardian gets a one-time password, then must replace it and accept their children before Lift will open."
          : null,
      ]
        .filter(Boolean)
        .join(". "),
    );
    setPreview(null);
    setFileName("");
    setOverwrite({});
    setError("");
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell shell--wide">
        <StaffNav />
        <p className="meta">
          Staff · {staff.given} {staff.family} · import
        </p>
        <h1>Import Students</h1>
        <p className="lede">
          Bring over this year's roster from the last system. Each imported
          student is saved on the {currentForm?.year ?? "current"} form, so
          families do not fill registration again. Matching uses first name,
          last name, and birthday. Guardians are linked by the email on the
          row. Their first password works once, and Lift stays closed until
          they replace it and accept the children assigned to them.
        </p>

        <div className="actions" style={{ marginTop: 0, marginBottom: "1rem" }}>
          <button type="button" onClick={downloadTemplate}>
            Download CSV template
          </button>
          <Link className="btn ghost" to="/staff">
            Back to roster
          </Link>
        </div>

        <div className="wire">
          <div className="meta">CSV file</div>
          <div className="field" style={{ marginTop: "0.75rem" }}>
            <label htmlFor="student-csv">Choose a spreadsheet export</label>
            <input
              id="student-csv"
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
            />
          </div>
          <div className="field" style={{ marginTop: "0.75rem" }}>
            <label htmlFor="student-csv-paste">Or paste CSV</label>
            <textarea
              id="student-csv-paste"
              rows={6}
              value={paste}
              onChange={(event) => setPaste(event.target.value)}
              placeholder="First name,Last name,Birthday,Email,..."
            />
          </div>
          <button type="button" className="ghost" onClick={onPasteReview}>
            Review pasted CSV
          </button>
          {fileName ? <div className="anno">{fileName}</div> : null}
        </div>

        {error ? <ErrorFlash key={error} message={error} /> : null}
        {result ? (
          <div className="flash flash--success" role="status">
            {result}
          </div>
        ) : null}

        {invites.length > 0 ? (
          <>
            <h2>Parent logins</h2>
            <p className="lede">
              Give each guardian the email and one-time password. They sign in,
              choose a different password, and accept the children on the
              account. They cannot continue past that screen.
            </p>
            <div className="table-wrap table-wrap--wide">
              <table className="table">
                <thead>
                  <tr>
                    <th>Guardian</th>
                    <th>Email</th>
                    <th>One-time password</th>
                    <th>Children to accept</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((invite) => (
                    <tr key={invite.email}>
                      <td>
                        {invite.given} {invite.family}
                      </td>
                      <td>{invite.email}</td>
                      <td>
                        {invite.password ? (
                          <input
                            readOnly
                            value={invite.password}
                            aria-label={`One-time password for ${invite.email}`}
                            onFocus={(event) => event.currentTarget.select()}
                          />
                        ) : invite.mustResetPassword ? (
                          "Already issued"
                        ) : (
                          "Keeps their current password"
                        )}
                      </td>
                      <td>{invite.studentNames.join(", ") || "Already linked"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
        {inviteSkips.length > 0 ? (
          <ul className="list-plain">
            {inviteSkips.map((skip) => (
              <li key={skip.email}>
                {skip.email}: {skip.reason}
              </li>
            ))}
          </ul>
        ) : null}

        {preview ? (
          <>
            <div className="grid-2" style={{ marginTop: "1.25rem" }}>
              <div className="wire">
                <div className="meta">New students</div>
                <strong style={{ fontSize: "1.5rem" }}>{preview.news.length}</strong>
                <div className="anno">Will be added to Lift</div>
              </div>
              <div className="wire dashed">
                <div className="meta">Already in Lift</div>
                <strong style={{ fontSize: "1.5rem" }}>
                  {preview.duplicates.length}
                </strong>
                <div className="anno">
                  Same name and birthday. Check a row to overwrite.
                </div>
              </div>
            </div>

            {preview.errors.length > 0 ? (
              <>
                <h2>Rows we could not read</h2>
                <ul className="list-plain">
                  {preview.errors.map((item) => (
                    <li key={`${item.line}-${item.message}`}>
                      Line {item.line}: {item.message}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {preview.duplicates.length > 0 ? (
              <>
                <h2>Overwrite existing students?</h2>
                <p className="lede">
                  These names and birthdays are already in Lift. Leave them
                  unchecked to keep the record you have. Check a box to replace
                  that student with the uploaded row.
                  {formAnswerCount > 0
                    ? " Medical notes, media release, and the rest of the permission form are saved onto each matching student either way."
                    : ""}
                </p>
                <div className="actions" style={{ marginTop: 0 }}>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => setAllOverwrites(true)}
                  >
                    Overwrite all
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => setAllOverwrites(false)}
                  >
                    Keep all existing
                  </button>
                </div>
                <div className="table-wrap table-wrap--wide">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Overwrite</th>
                        <th>Student</th>
                        <th>Birthday</th>
                        <th>In Lift now</th>
                        <th>Uploaded row</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.duplicates.map((row) => (
                        <DuplicateRow
                          key={row.key}
                          row={row}
                          checked={Boolean(overwrite[row.key])}
                          onChange={(checked) =>
                            setOverwrite((current) => ({
                              ...current,
                              [row.key]: checked,
                            }))
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            {preview.news.length > 0 ? (
              <>
                <h2>New students</h2>
                <div className="table-wrap table-wrap--wide">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Birthday</th>
                        <th>School</th>
                        <th>Grade</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.news.map((row) => (
                        <tr key={row.key}>
                          <td>
                            {row.parsed.input.given} {row.parsed.input.family}
                          </td>
                          <td>{formatBirthdayLabel(row.parsed.input.birthday)}</td>
                          <td>{row.parsed.input.school}</td>
                          <td>{row.parsed.input.grade}</td>
                          <td>{row.parsed.input.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            <div className="actions">
              <button type="button" onClick={importRows}>
                {rosterCount > 0
                  ? `Import ${rosterCount} student${rosterCount === 1 ? "" : "s"}`
                  : `Save form answers for ${formAnswerCount} student${formAnswerCount === 1 ? "" : "s"}`}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setPreview(null);
                  setFileName("");
                  setOverwrite({});
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}

function DuplicateRow({
  row,
  checked,
  onChange,
}: {
  row: DuplicateImportRow;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { existing, parsed } = row;
  return (
    <tr>
      <td>
        <label>
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
          />{" "}
          Overwrite
        </label>
      </td>
      <td>
        {parsed.input.given} {parsed.input.family}
      </td>
      <td>{formatBirthdayLabel(parsed.input.birthday)}</td>
      <td>
        {existing.school} · grade {existing.grade}
        <div className="anno">{existing.email}</div>
      </td>
      <td>
        {parsed.input.school} · grade {parsed.input.grade}
        <div className="anno">{parsed.input.email}</div>
      </td>
    </tr>
  );
}
